import { Flex, Form, Modal } from 'antd';
import {
  MButton,
  MCheckbox,
  MInput,
  MRadio,
  MTextArea,
} from '@Components/Base';

import { useAppSelector } from '@Hooks/index';
import {
  getModalParams,
  getModalRecordKey,
} from '@Store/selectors/modalSelector';
import { FormikHelpers, useFormik } from 'formik';
import { DateRange } from '@Components/Filters';
import {
  getMrForDict,
  getTenderModeDict,
} from '@Store/selectors/dictionarySelector';
import { useCallback, useEffect, useMemo, useState } from 'react';
import * as Yup from 'yup';
import { TenderSearchVendorsResponse } from '@Types/api/form/tenderRequest/TenderSearchVendorsResponse';
import styles from './RequestInitiationModal.module.css';
import { InfoCard } from '../InfoCard';
import {
  useSearchTenderVendorsQuery,
  useSubmitTenderMutation,
} from '../../TenderRequest/store/api/TenderRequestApiSlice';
import {
  InitiateForm,
  mapInitiateFormData,
} from '../../TenderRequest/form/utils/initiateTender';
import { findTenderInitiationVendorsBySearchValue } from '../../utils/finder';

interface RequestInitiationModalProps {
  isOpen: boolean;
  onCancel: () => void;
  onSuccess?: () => void;
}

const initialValues: InitiateForm = {
  startDate: '',
  endDate: '',
  purchaseFor: 'Order',
  tenderMode: 'Auto',
  tenderComments: '',
  selectedVendors: [],
};

const RequestInitiationModal: React.FC<RequestInitiationModalProps> = ({
  isOpen,
  onCancel,
  onSuccess,
}) => {
  const modalRecordKey = useAppSelector(getModalRecordKey);
  const modalParams = useAppSelector(getModalParams);
  const prForTypes = useAppSelector(getMrForDict);
  const tenderModeTypes = useAppSelector(getTenderModeDict);

  const [submitTender] = useSubmitTenderMutation();
  const [search, setSearch] = useState('');
  const [searchResult, setSearchResult] =
    useState<TenderSearchVendorsResponse[]>();

  const handleSubmitTenderInitiateForm = (
    values: InitiateForm,
    formikHelpers: FormikHelpers<InitiateForm>
  ): void => {
    const tenderData = mapInitiateFormData(values, prForTypes, tenderModeTypes);

    submitTender({ ...tenderData, requestId: Number(modalRecordKey) }).then(
      () => {
        onSuccess?.();
        onCancel();
        formikHelpers.resetForm();
      }
    );
  };

  const formik = useFormik({
    initialValues: {
      ...initialValues,
      purchaseFor:
        (modalParams?.prFor as 'Order' | 'Submittal') ||
        initialValues.purchaseFor,
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      selectedVendors: Yup.array().test(
        'selected-vendors-rule',
        'At least one vendor should be selected for Manual mode during Initiation',
        (selectedVendorsValue, context) => {
          const { tenderMode } = context.parent;
          if (tenderMode === 'Auto') {
            return true;
          }
          if (tenderMode === 'Manual') {
            return selectedVendorsValue && selectedVendorsValue?.length !== 0;
          }
          return true;
        }
      ),
    }),
    onSubmit: handleSubmitTenderInitiateForm,
  });

  const { data } = useSearchTenderVendorsQuery(
    {
      tenderId: Number(modalRecordKey),
      tenderMode: formik.values.tenderMode === 'Auto' ? 1 : 2,
    },
    { skip: !modalRecordKey || !formik.values.tenderMode }
  );

  useEffect(() => {
    if (data?.length && formik.values.tenderMode === 'Auto') {
      formik.setFieldValue('selectedVendors', data, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, formik.values.tenderMode]);

  const handleSearchVendors = useCallback(
    (value: string) => {
      setSearch(value);

      if (data) {
        const result = findTenderInitiationVendorsBySearchValue(data, value);
        setSearchResult(result);
      }
    },
    [data]
  );

  const dataToDisplay = useMemo(() => {
    if (search) {
      return searchResult;
    }
    return data;
  }, [data, search, searchResult]);

  const customButtons = [
    <MButton key="back" type="tertiary" onClick={onCancel}>
      Cancel
    </MButton>,
    <MButton key="submit" type="primary" onClick={formik.submitForm}>
      Initiate
    </MButton>,
  ];

  return (
    <Modal
      open={isOpen}
      className={styles.modal}
      footer={customButtons}
      width={600}
      title={`RFQ Initiation - Tender No. ${modalRecordKey}`}
      closable
      onCancel={() => {
        onCancel();
        formik.resetForm();
      }}
      destroyOnClose
    >
      <div className={styles.main_form}>
        <DateRange
          formik={formik}
          fromKey="startDate"
          toKey="endDate"
          fromLabel="Start Date"
          toLabel="End Date"
          layout="vertical"
          dataPickerClassName={styles.data_picker}
        />
        <Form.Item
          className={styles.description}
          label="Purchase For"
          layout="vertical"
        >
          <Flex>
            {prForTypes.map((type) => (
              <MRadio
                key={type.codeNum}
                id={type.codeDesc}
                labelText={type.codeDesc}
                value={type.codeDesc}
                checked={formik.values.purchaseFor === type.codeDesc}
                className={styles.radio_button}
                disabled
              />
            ))}
          </Flex>
        </Form.Item>
        <Form.Item
          className={styles.description}
          label="Tender Comments"
          layout="vertical"
        >
          <MTextArea
            placeholder="Insert Comments"
            rows={4}
            style={{ height: 100 }}
            {...formik.getFieldProps('tenderComments')}
          />
        </Form.Item>
        <Form.Item
          className={styles.description}
          label="Supplier Selection"
          layout="vertical"
        >
          <Flex>
            {tenderModeTypes.map((type) => (
              <MRadio
                key={type.codeNum}
                id={type.codeDesc}
                labelText={type.codeDesc}
                value={type.codeDesc}
                checked={
                  formik.getFieldProps('tenderMode').value === type.codeDesc
                }
                onChange={(e) => {
                  formik.setFieldValue('tenderMode', e.target.value);
                  if (e.target.value === 'Auto') {
                    setSearch('');
                    formik.setFieldValue('selectedVendors', data, false);
                  } else {
                    setSearch('');
                    formik.setFieldValue('selectedVendors', [], false);
                  }
                }}
                className={styles.radio_button}
              />
            ))}
          </Flex>
        </Form.Item>
      </div>
      <Flex className={styles.main} vertical>
        <Form.Item
          className={styles.description}
          label="Select the vendors to Initiate the RFQ"
          layout="vertical"
          validateStatus={formik.errors.selectedVendors ? 'error' : ''}
          help={(formik.errors.selectedVendors as string) ?? ''}
        >
          <MInput
            className={
              formik.errors.selectedVendors ? undefined : styles.search
            }
            searchInput
            placeholder="Search..."
            allowClear
            value={search}
            onChange={(e) => {
              handleSearchVendors(e.target.value);
            }}
          />
        </Form.Item>
        <Flex vertical gap={16}>
          {dataToDisplay?.map((vendor) => (
            <InfoCard
              key={vendor.vendorId}
              title={
                <MCheckbox
                  disabled={formik.values.tenderMode === 'Auto'}
                  className={styles.checkbox_disabled}
                  onClick={() => {
                    const isSelected = formik.values.selectedVendors.find(
                      (selected) => selected.vendorId === vendor.vendorId
                    );
                    if (isSelected) {
                      formik.setFieldValue(
                        'selectedVendors',
                        formik.values.selectedVendors.filter(
                          (selected) => selected.vendorId !== vendor.vendorId
                        )
                      );
                    } else {
                      formik.setFieldValue('selectedVendors', [
                        ...formik.values.selectedVendors,
                        vendor,
                      ]);
                    }
                  }}
                  checked={Boolean(
                    formik.values.selectedVendors.find(
                      (selected) => selected.vendorId === vendor.vendorId
                    )
                  )}
                >
                  {vendor.vendorName}
                </MCheckbox>
              }
              items={[{ label: 'Vendor ID', value: String(vendor.vendorId) }]}
            />
          ))}
        </Flex>
      </Flex>
    </Modal>
  );
};

export default RequestInitiationModal;
